import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Ref } from 'vue';
import { useNuxtApp } from '#imports';
import { useCheckoutStore } from './checkout';
import { CART_COOKIE_NAME } from '@/resources/helpers';

export const useCartStore = defineStore('cart', () => {
  const { $logger, $mixpanel } = useNuxtApp();
  const checkoutStore = useCheckoutStore();
  const cartId: Ref<Record<string, any>> = ref({});
  const cartLoading: Ref<boolean> = ref(false);
  const cart: Ref<Record<string, any>> = ref({});
  const cartItemErrors = ref<Record<string, string>>({});
  const distinctId = computed(() => $mixpanel.get_distinct_id());

  const transformCart = (cart) => {
    let total = cart?.grand_total - cart?.already_paid || 0;
    let giftcardsTotal = 0;

    return {
      items: cart?.items?.map((item) => {
        item.quantity = 1;
        item._id = item.item_id;
        return item;
      }),
      subtotal: cart?.subtotal,
      total,
      shippingTotal: cart?.shipping_amount,
      orderPromotionTotal: cart?.discount_amount,
      promotionCoupon: cart?.coupon_code || null,
      giftcards: cart?.giftcards || null,
      giftcardsTotal,
      totalSegments: cart?.total_segments
    };
  };

  const createCart = async (): Promise<string | null> => {
    try {
      const { result } = await $fetch('/api/cart/createCart', {
        method: 'POST',
        body: {
          distinctId: distinctId.value
        }
      }).catch((error) => {
        throw new Error(error);
      });
      cartId.value = result;
      if (import.meta.client) setCartId(result);
      return cartId.value ? String(cartId.value) : null;
    } catch (err) {
      $logger.error('error in create cart', err);
      return null;
    }
  };

  const getCartId = () => localStorage.getItem(CART_COOKIE_NAME);

  function setCartId(id: string) {
    if (!id) {
      localStorage.removeItem(CART_COOKIE_NAME);
      return;
    }
    localStorage.setItem(CART_COOKIE_NAME, id);
  }

  async function getCart(cartId: string, isUpdating?: boolean) {
    cartLoading.value = true;
    try {
      const data = await $fetch(`/api/cart/getCart`, {
        query: { cartId }
      }).catch((error) => {
        throw new Error(error);
      });

      if (data?.result) {
        cart.value = transformCart(data.result);

        // Clear all errors when cart is successfully loaded
        cartItemErrors.value = {};

        if (window?.localStorage) {
          const cartItems = localStorage.getItem('cart-items');
          if (cartItems && typeof cartItems === 'string' && cartItems !== 'undefined') {
            const cartItemsArray = JSON.parse(cartItems);
            cartItemsArray.forEach((item) => {
              const cartItem = cart.value.items.find((cartItem) => cartItem.sku === item.sku);
              const index = cart.value.items.findIndex((cartItem) => cartItem.sku === item.sku);
              if (cartItem && !isUpdating) {
                if (item.qty > cartItem.qty) {
                  cart.value.items[index].qty_changed = true;
                }
              }
            });
            // }
          }
          localStorage.setItem('cart-items', JSON.stringify(cart.value?.items));
        }

        return data;
      }
    } catch (err) {
      $logger.error('errors in getCart', err);
    } finally {
      cartLoading.value = false;
    }
  }

  async function updateCart({ cartId, product }) {
    cartLoading.value = true;
    try {
      const result = await $fetch(`/api/cart/updateCart`, {
        method: 'POST',
        body: {
          cartId,
          cartItem: product.cartItem,
          distinctId: distinctId.value
        }
      }).catch((error) => {
        throw new Error(error);
      });
      if (result?.code === 400 && result?.message?.length) {
        // errorCartAmountSelected.value = result?.message;
        cartItemErrors.value[product.cartItem.sku] = result?.message;
      } else {
        // errorCartAmountSelected.value = null;
        // Only clear the error for this specific SKU when update is successful
        cartItemErrors.value[product.cartItem.sku] = null;

        // update cart items in local storage
        if (window?.localStorage) {
          const cartItems = localStorage.getItem('cart-items');
          if (cartItems) {
            const cartItemsArray = JSON.parse(cartItems);
            cartItemsArray.forEach((item) => {
              const cartItem = cart.value?.items?.find((cartItem) => cartItem.sku === product.cartItem.sku);
              if (cartItem) {
                return;
                item.qty = product.cartItem.qty;
                return;
              }
            });
            localStorage.setItem('cart-items', JSON.stringify(cartItemsArray));
          }
        }
      }
      return result.result;
    } catch (err) {
      $logger.error('error in updatecart store', err);
    } finally {
      cartLoading.value = false;
    }
  }

  async function loadCart() {
    let cartId = getCartId();
    cartLoading.value = true;
    try {
      if (!cartId) {
        cartId = await createCart().catch((error) => {
          throw new Error(error);
        });
        if (!cartId) return cart.value;
        setCartId(cartId);
      }

      const response = await getCart(cartId).catch((error) => {
        throw new Error(error);
      });
      // empty response means cart token is no longer valid
      if (
        response?.result === undefined ||
        (response?.result && Object.keys(response?.result).length === 0) ||
        response?.code === 400
      ) {
        cartId = await createCart().catch((error) => {
          throw new Error(error);
        });
        if (cartId) {
          setCartId(cartId);
          await getCart(cartId).catch((error) => {
            throw new Error(error);
          });
        }
        return cart.value;
      }
      return response?.result;
    } catch (err) {
      $logger.error('Error in load cart', err);
    } finally {
      cartLoading.value = false;
    }
  }

  async function addItem({ product, quantity, currentCart }) {
    cartLoading.value = true;
    let cartId = getCartId();
    if (!cartId) {
      await loadCart().catch((error) => {
        throw new Error(error);
      });
      cartId = getCartId();
    }

    try {
      const existingItem = null;
      if (currentCart?.items) {
        currentCart.items.find((item) => item.sku === product.sku);
      }
      const cartItem: any = {
        sku: product.code,
        quoteId: cartId,
        qty: existingItem ? existingItem.qty + quantity : quantity
      };

      if (product?.product_option !== undefined) {
        cartItem.product_option = product.product_option;
        // cartItem.item_id = parseInt(String(product.id).replace('/api/v2/products/', ''));
      }

      await updateCart({
        cartId,
        product: {
          cartItem,
          product
        }
      });

      await getCart(cartId).catch((error) => {
        throw new Error(error);
      });

      return cart.value;
    } catch (err) {
      $logger.error('Error in adding item to cart', err);
      cartId = await createCart().catch((error) => {
        throw new Error(error);
      });
      setCartId(cartId);
      return await addItem({ product, quantity, currentCart }).catch((error) => {
        throw new Error(error);
      });
    } finally {
      cartLoading.value = false;
    }
  }

  async function removeItem({ cartId, product }) {
    cartLoading.value = true;
    try {
      await $fetch('/api/cart/removeCartItem', {
        method: 'POST',
        body: {
          cartId,
          ...product,
          distinctId: distinctId.value
        }
      }).catch((error) => {
        throw new Error(error);
      });
      const cart = await getCart(cartId).catch((error) => {
        throw new Error(error);
      });
      return cart.result;
    } catch (err) {
      $logger.error('Error in remove item', err);
    } finally {
      cartLoading.value = false;
    }
  }

  async function updateItemQuantity({ product, quantity }) {
    cartLoading.value = true;
    const cartId = getCartId();
    try {
      await updateCart({
        cartId,
        product: {
          cartItem: {
            sku: product.sku,
            quoteId: cartId,
            qty: quantity,
            product_option: product.product_option
          }
        }
      }).catch((error) => {
        throw new Error(error);
      });

      const newCart = await getCart(cartId, true).catch((error) => {
        throw new Error(error);
      });

      const productData = newCart.result.items;
      cart.value.items = productData;
      return cart.value;
    } catch (err) {
      $logger.error('Error in update cart quantity', err);
    } finally {
      cartLoading.value = false;
    }
  }

  async function applyCoupon({ coupon }: { coupon: string }, { email }: { email: string }) {
    cartLoading.value = true;
    const cartId = getCartId();
    try {
      const result = await $fetch('/api/cart/applyCoupon', {
        method: 'POST',
        body: {
          email,
          cartId,
          coupon
        }
      }).catch((error) => {
        throw new Error(error);
      });

      // need to check updatedCart overall structure
      if (result.code === 200 && cartId) {
        checkoutStore.error = false;
        await getCart(cartId).catch((error) => {
          throw new Error(error);
        });
        return cart.value;
      }

      throw new Error('Coupon code "' + coupon + '" could not be applied');
    } catch (err) {
      $logger.error('Error in applying coupon:', err);
      checkoutStore.error = true;
    } finally {
      cartLoading.value = false;
    }
  }

  async function removeGiftCard(giftCardCode) {
    cartLoading.value = true;
    const cartId = getCartId();
    try {
      const result = $fetch('/api/cart/removeGiftCardCode', {
        method: 'POST',
        body: {
          cartId,
          giftCardCode
        }
      }).catch((error) => {
        throw new Error(error);
      });
      cart.value = await getCart(cartId).catch((error) => {
        throw new Error(error);
      });
      return await getCart(cartId).catch((error) => {
        throw new Error(error);
      });
    } catch (err) {
      $logger.error('Error in removing giftcard', err);
    } finally {
      cartLoading.value = false;
    }
  }

  async function removeCoupon() {
    const cartId = getCartId();
    cartLoading.value = true;

    try {
      $fetch('/api/cart/removeCouponCode', {
        method: 'POST',
        body: {
          cartId
        }
      })
        .then(() => getCart(cartId))
        .catch((error) => {
          throw new Error(error);
        });

      return cart.value;
    } catch (err) {
      $logger.error('Error in removing giftcard', err);
    } finally {
      cartLoading.value = false;
    }
  }

  async function clearCart() {
    setCartId(null);
    const cartId = await createCart().catch((error) => {
      throw new Error(error);
    });
    setCartId(cartId);
    await getCart(cartId).catch((error) => {
      throw new Error(error);
    });
    return cart.value;
  }

  const productsInCart = computed(() => cart.value?.items || []);

  const isPhysicalGutschein = computed(() =>
    productsInCart?.value
      ?.map((product) => product.product_type === 'giftcard' && product.sku?.includes('Physischer-Gutschein'))
      .filter((val) => val === true)
  );

  // Add function to clear error for specific SKU
  function clearItemError(sku: string) {
    if (cartItemErrors.value[sku]) {
      cartItemErrors.value[sku] = null;
    }
  }

  return {
    createCart,
    getCartId,
    setCartId,
    getCart,
    cartLoading,
    cart,
    updateCart,
    addItem,
    loadCart,
    removeItem,
    updateItemQuantity,
    applyCoupon,
    removeGiftCard,
    removeCoupon,
    clearCart,
    clearItemError,
    // errorCartAmountSelected,
    cartItemErrors,
    productsInCart,
    isPhysicalGutschein
  };
});
